import React, { useEffect, useState, Suspense, lazy, Component, ErrorInfo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster, toast as hotToast } from 'react-hot-toast';
import { KeycloakProvider, useKeycloak } from './contexts/KeycloakContext';
import { AuthGuard } from './components/AuthGuard';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerFilterProvider } from './contexts/CustomerFilterContext';
import { CustomerProvider, useCustomer } from './contexts/CustomerContext';
import { PersonDetailProvider } from './contexts/PersonDetailContext';
import { PersonDetailModal } from './components/PersonDetailModal';
import { NotificationPreferencesProvider } from './contexts/NotificationPreferencesContext';
import { OperationalSettingsProvider } from './contexts/OperationalSettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ConnectionStatusProvider } from './contexts/ConnectionStatusContext';
import { SystemHealthProvider } from './contexts/SystemHealthContext';
import { CriticalErrorProvider } from './contexts/CriticalErrorContext';
import { AccessDeniedProvider } from './contexts/AccessDeniedContext';
import ErrorOverlay from './components/ErrorOverlay';
import RoleBasedRoute from './components/RoleBasedRoute';
import { RoleCategory, getUserRoleCategory, isAdmin, isManager, isOwner } from './config/roles';
import { initializeConsoleInterface } from './services/loggerConsole';
import Layout from './components/Layout';
import toast from 'react-hot-toast';
import * as menuActions from './services/menuActions';
import UpdateStatusModal, { UpdateStatusPayload } from './components/UpdateStatusModal';
import { registerWebDevice } from './services/api';
import LoadingScreen from './components/LoadingScreen';
import NetworkStatusBanner from './components/NetworkStatusBanner';
import { ConfirmDialogProvider, useConfirm } from './components/ui/ConfirmDialog';
import { SyncProgressProvider } from './contexts/SyncProgressContext';
import SyncProgressModal from './pages/SyncProgress';
import { ToastCard, toastToneFor, toastTitleFor } from './components/ui/ToastCard';
import { SyncProgressCardSlot } from './components/SyncProgressCardSlot';
import DesktopNotAvailable from './components/DesktopNotAvailable';
import AccessPicker from './components/AccessPicker';

// Core pages - loaded eagerly for fast navigation
import Dashboard from './pages/Dashboard';

// Lazy-loaded pages - loaded on demand to reduce initial bundle size
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const SindicoDashboard = lazy(() => import('./pages/SindicoDashboard'));
const OwnerDashboard = lazy(() => import('./pages/OwnerDashboard'));
const People = lazy(() => import('./pages/People'));

// Public pages - No authentication required
const LandingPage = lazy(() => import('./pages/LandingPage'));
const CustomerLandingPage = lazy(() => import('./pages/CustomerLandingPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const PoliticaCSAE = lazy(() => import('./pages/PoliticaCSAE'));
const SolicitarExclusao = lazy(() => import('./pages/SolicitarExclusao'));
const Suporte = lazy(() => import('./pages/Suporte'));
const AndroidInstall = lazy(() => import('./pages/AndroidInstall'));
const PublicPreRegistration = lazy(() => import('./pages/PublicPreRegistration'));
const InviteRegistration = lazy(() => import('./pages/InviteRegistration'));
const ResidentSelfRegistration = lazy(() => import('./pages/ResidentSelfRegistration'));
const PublicGuestList = lazy(() => import('./pages/PublicGuestList'));
const Zones = lazy(() => import('./pages/Zones'));
const ZonesDevices = lazy(() => import('./pages/ZonesDevices'));
const Facial = lazy(() => import('./pages/Facial'));
const Customers = lazy(() => import('./pages/Customers'));
const Condos = lazy(() => import('./pages/Condos'));
const Blocks = lazy(() => import('./pages/Blocks'));
const Units = lazy(() => import('./pages/Units'));
const CommonAreas = lazy(() => import('./pages/CommonAreas'));
const Residents = lazy(() => import('./pages/Residents'));
const CondoEmployees = lazy(() => import('./pages/CondoEmployees'));
const Visitors = lazy(() => import('./pages/Visitors'));
const Liberados = lazy(() => import('./pages/Liberados'));
const Solicitacoes = lazy(() => import('./pages/Solicitacoes'));
const ServiceProviders = lazy(() => import('./pages/ServiceProviders'));
const Departments = lazy(() => import('./pages/Departments'));
const DepartmentDetail = lazy(() => import('./pages/DepartmentDetail'));
const Employees = lazy(() => import('./pages/Employees'));
const Staff = lazy(() => import('./pages/Staff'));
const Roles = lazy(() => import('./pages/Roles'));
const UserAdministration = lazy(() => import('./pages/UserAdministration'));
const UnifiedUserAdmin = lazy(() => import('./pages/UnifiedUserAdmin'));
const AppVersionPolicies = lazy(() => import('./pages/AppVersionPolicies'));
const CustomerAdministration = lazy(() => import('./pages/CustomerAdministration'));
const CustomerSettings = lazy(() => import('./pages/CustomerSettings'));
const ConnectedDevices = lazy(() => import('./pages/ConnectedDevices'));
const ImportData = lazy(() => import('./pages/ImportData'));
const ImportDataFileWizard = lazy(() => import('./pages/ImportDataFileWizard'));
const ImportDataDeviceWizard = lazy(() => import('./pages/ImportDataDeviceWizard'));
const ExportData = lazy(() => import('./pages/ExportData'));
const AccessLogs = lazy(() => import('./pages/AccessLogs'));
const AccessControl = lazy(() => import('./pages/AccessControl'));
const EventosGerais = lazy(() => import('./pages/EventosGerais'));
const AccessRules = lazy(() => import('./pages/AccessRules'));
const Profile = lazy(() => import('./pages/Profile'));
const Support = lazy(() => import('./pages/Support'));
// Devices page removed - use ZonesDevices instead
const ZoneGroups = lazy(() => import('./pages/ZoneGroups'));
const TimeZones = lazy(() => import('./pages/TimeZones'));
const AccessProfiles = lazy(() => import('./pages/AccessProfiles'));
const AdminDeviceMigration = lazy(() => import('./pages/AdminDeviceMigration'));
const UnauthorizedPersons = lazy(() => import('./pages/UnauthorizedPersons'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const CustomerStats = lazy(() => import('./pages/CustomerStats'));
const GlobalDashboard = lazy(() => import('./pages/GlobalDashboard'));
const GlobalReports = lazy(() => import('./pages/GlobalReports'));
const RegistrationCameras = lazy(() => import('./pages/RegistrationCameras'));
const PersonProfile = lazy(() => import('./pages/PersonProfile'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const DeviceAuditLogs = lazy(() => import('./pages/DeviceAuditLogs'));
const CustomerQRCodeManagement = lazy(() => import('./pages/CustomerQRCodeManagement'));
const Encomendas = lazy(() => import('./pages/Encomendas'));
const Integrations = lazy(() => import('./pages/Integrations'));
const CommonAreaReservations = lazy(() => import('./pages/CommonAreaReservations'));
const Obras = lazy(() => import('./pages/Obras'));
const Keys = lazy(() => import('./pages/Keys'));
const Pets = lazy(() => import('./pages/Pets'));
const Tags = lazy(() => import('./pages/Tags'));
const NormasInternas = lazy(() => import('./pages/NormasInternas'));
const Ocorrencias = lazy(() => import('./pages/Ocorrencias'));
const Livro = lazy(() => import('./pages/Livro'));
const AchadosPerdidos = lazy(() => import('./pages/AchadosPerdidos'));
const MuralAnuncios = lazy(() => import('./pages/MuralAnuncios'));
const Reconciliation = lazy(() => import('./pages/Reconciliation'));
const PersonDevices = lazy(() => import('./pages/PersonDevices'));
const RoleAuditPage = lazy(() => import('./pages/RoleAuditPage'));
const Downloads = lazy(() => import('./pages/Downloads'));
const PhotoQualityAuditPage = lazy(() => import('./pages/PhotoQualityAuditPage'));
const KioskPage = lazy(() => import('./pages/KioskPage'));
const KioskManagement = lazy(() => import('./pages/KioskManagement'));
const AvisosAtualizacoes = lazy(() => import('./pages/AvisosAtualizacoes'));

// Top-level error boundary to show errors instead of black screen
class AppErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ background: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <div style={{ maxWidth: 520, width: '100%' }}>
            <h2 style={{ color: '#dc2626', fontFamily: 'sans-serif', marginBottom: 12 }}>Erro na aplicação</h2>
            <pre style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', color: '#991b1b', marginBottom: 16 }}>
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
            <button
              onClick={() => { this.setState({ error: null }); window.location.reload(); }}
              style={{ background: '#111', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontFamily: 'sans-serif', fontSize: 14 }}
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading fallback component
const PageLoading = () => (
  <div className="flex items-center justify-center h-64">
    <div className="flex flex-col items-center">
      {/* Loading Spinner */}
      <div className="mb-4">
        <div className="w-12 h-12 border-4 border-[#96bf13] border-t-transparent rounded-full animate-spin"></div>
      </div>

      <p className="text-gray-600 text-sm">
        Carregando...
      </p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,      // 5 min default - data considered fresh
      cacheTime: 30 * 60 * 1000,     // 30 min - keep in cache before garbage collection
    },
  },
});

// Full-page loading component for app initialization
const AppInitLoading: React.FC = () => (
  <LoadingScreen variant="full" message="Carregando..." showLogo={true} />
);

// Gates the authenticated app tree for non-admin users: blocks morador/funcionario-only
// accounts entirely (desktop is for operational staff — see DesktopNotAvailable), and
// shows a role picker before rendering anything when the user holds more than one
// distinct operational role and hasn't chosen one yet (or reopened the picker). Admins
// are never gated (CustomerContext resets these flags for admins). Waits for
// isInitialized so it never flashes a blocking screen before the assignment fetch
// resolves.
const DesktopAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isInitialized, desktopAccessBlocked, pendingRoleChoice } = useCustomer();

  if (!isInitialized) {
    return <>{children}</>;
  }
  if (desktopAccessBlocked) {
    return <DesktopNotAvailable />;
  }
  if (pendingRoleChoice) {
    return <AccessPicker />;
  }
  return <>{children}</>;
};

// Role-based Dashboard Router Component
const DashboardRouter: React.FC = () => {
  const { roles } = useKeycloak();
  const { selectedCustomer, isInitialized, activeRole } = useCustomer();

  // Respect view mode simulations set from PersonalSettings
  const isSindicoMode = localStorage.getItem('isSindicoMode') === 'true';
  const isMoradorMode = localStorage.getItem('isMoradorMode') === 'true';
  const isFuncionarioMode = localStorage.getItem('isFuncionarioMode') === 'true';

  // Union with activeRole (not a replacement): operational roles assigned via
  // "Atribuições" (UserCustomerAssignment, DB-only) never touch the Keycloak JWT, so a
  // porteiro/sindico/zelador/supervisor/proprietario assigned that way would otherwise
  // always evaluate to roleCategory=null here and fall through to the generic
  // OwnerDashboard instead of their actual role's dashboard.
  const effectiveRoles = isSindicoMode
    ? ['sindico']
    : isMoradorMode
    ? ['morador']
    : isFuncionarioMode
    ? ['porteiro']
    : activeRole
    ? [...roles, activeRole]
    : roles;
  const roleCategory = getUserRoleCategory(effectiveRoles);

  // Wait for customer context to be fully initialized before rendering any dashboard
  if (!isInitialized) {
    return <AppInitLoading />;
  }

  // Redirect to appropriate dashboard based on (effective) role and customer selection
  if (roleCategory === RoleCategory.ADMIN) {
    if (!selectedCustomer) {
      return <GlobalDashboard key="global-dashboard" />;
    }
    return <AdminDashboard key={`admin-dashboard-${selectedCustomer.id}`} />;
  } else if (roleCategory === RoleCategory.MANAGER) {
    return <SindicoDashboard key={selectedCustomer ? `sindico-${selectedCustomer.id}` : 'sindico'} />;
  } else if (roleCategory === RoleCategory.RESIDENT) {
    return <OwnerDashboard key="owner-dashboard" />;
  } else if (roleCategory === RoleCategory.STAFF) {
    return <Dashboard key="staff-dashboard" />;
  } else {
    return <OwnerDashboard key="default-owner-dashboard" />;
  }
};

/**
 * Registers the current browser/Electron session with the backend once per login.
 * Must be inside AuthGuard (authenticated context).
 */
const DeviceRegistrationEffect: React.FC = () => {
  const { authenticated } = useKeycloak();
  useEffect(() => {
    if (!authenticated) return;
    // Fire-and-forget — registration failures are non-fatal
    registerWebDevice().catch(() => {});
  }, [authenticated]);
  return null;
};

/**
 * Menu Event Listeners Component
 *
 * Registers Electron menu event listeners when running in Electron desktop app.
 * Must be inside CustomerProvider to access selectedCustomer.
 */
const MenuEventListeners: React.FC = () => {
  const { selectedCustomer } = useCustomer();
  const confirm = useConfirm();

  useEffect(() => {
    // Only run in Electron environment
    if (!window.electronAPI?.isElectron()) {
      return;
    }

    // Check if onMenuAction is available (type assertion for build compatibility)
    const electronAPI = window.electronAPI as any;
    if (!electronAPI.onMenuAction) {
      console.warn('[MenuEventListeners] onMenuAction not available');
      return;
    }

    // Helper functions for menu actions
    const showToast = (message: string, type?: 'success' | 'error' | 'info') => {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else {
        toast(message);
      }
    };

    const showConfirmation = async (message: string): Promise<boolean> => {
      return confirm({ message, variant: 'warning', confirmLabel: 'Confirmar' });
    };

    // Get customer ID
    const customerId = selectedCustomer?.id || null;

    // Register all menu event listeners
    const removeListeners = [
      // Visitor Management
      electronAPI.onMenuAction('menu:force-revoke-visitors', () => {
        menuActions.handleForceRevokeVisitors(customerId, showConfirmation, showToast);
      }),
      electronAPI.onMenuAction('menu:checkout-all-visitors', () => {
        menuActions.handleCheckoutAllVisitors(customerId, showConfirmation, showToast);
      }),
      electronAPI.onMenuAction('menu:end-of-day-cleanup', () => {
        menuActions.handleEndOfDayCleanup(customerId, showConfirmation, showToast);
      }),

      // System Operations
      electronAPI.onMenuAction('menu:check-device-connectivity', () => {
        menuActions.handleCheckDeviceConnectivity(customerId, showToast);
      }),
      electronAPI.onMenuAction('menu:system-diagnostics', () => {
        menuActions.handleSystemDiagnostics(showToast);
      }),
      electronAPI.onMenuAction('menu:clear-cache', () => {
        menuActions.handleClearCache(showConfirmation, showToast);
      }),

      // Data Export
      electronAPI.onMenuAction('menu:export-today-visitors', () => {
        menuActions.handleExportTodayVisitors(customerId, showToast);
      }),
      electronAPI.onMenuAction('menu:export-access-logs', () => {
        menuActions.handleExportAccessLogs(customerId, showToast);
      }),

      // Reports
      electronAPI.onMenuAction('menu:daily-report', () => {
        menuActions.handleDailyReport(customerId, showToast);
      }),
      electronAPI.onMenuAction('menu:access-statistics', () => {
        menuActions.handleAccessStatistics(customerId, showToast);
      }),
    ];

    // Cleanup listeners on unmount
    return () => {
      removeListeners.forEach(remove => remove());
    };
  }, [selectedCustomer]);

  // This component doesn't render anything
  return null;
};

/**
 * Listens for update:status events pushed from the main process and renders
 * the in-app UpdateStatusModal instead of native dialog boxes.
 */
const UpdateStatusListener: React.FC = () => {
  const [payload, setPayload] = React.useState<UpdateStatusPayload | null>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const electronAPI = (window as any).electronAPI;
    if (!electronAPI?.onUpdateStatus) return;
    const unsubscribe = electronAPI.onUpdateStatus((data: UpdateStatusPayload) => {
      setPayload(data);
      // Broadcast update badge state to Layout and other components
      if (data.type === 'update-available' || data.type === 'update-downloaded') {
        window.dispatchEvent(new CustomEvent('panther:update-available', { detail: { type: data.type } }));
      } else if (data.type === 'up-to-date') {
        window.dispatchEvent(new CustomEvent('panther:update-cleared'));
      }
    });
    return unsubscribe;
  }, []);

  return (
    <UpdateStatusModal
      payload={payload}
      onClose={() => setPayload(null)}
      onNavigateToAvisos={() => navigate('/avisos-atualizacoes')}
    />
  );
};

function App() {
  // Incrementing this key forces KeycloakProvider to fully re-mount (all state
  // reset to initial values), triggering a fresh auth init without a page reload.
  // NetworkStatusBanner dispatches 'panther:retry-auth' to trigger this.
  const [retryKey, setRetryKey] = useState(0);
  const [lightThemeActive, setLightThemeActive] = useState(() => {
    try {
      const saved = localStorage.getItem('operational_view_settings');
      if (saved) return JSON.parse(saved).theme === 'light';
    } catch {}
    return false;
  });

  useEffect(() => {
    const handler = () => setRetryKey(k => k + 1);
    window.addEventListener('panther:retry-auth', handler);
    return () => window.removeEventListener('panther:retry-auth', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => setLightThemeActive((e as CustomEvent).detail.isLight);
    window.addEventListener('panther:theme-changed', handler);
    return () => window.removeEventListener('panther:theme-changed', handler);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('panther-light-toast', lightThemeActive);
  }, [lightThemeActive]);

  useEffect(() => {
    initializeConsoleInterface();

    document.title = 'Panther Access - Controle de Acesso';

    // Log version info in dev mode
    if (process.env.NODE_ENV === 'development') {
      console.log(' Panther Access Version Info:', {
        version: process.env.REACT_APP_VERSION || '0.1.0',
        commit: process.env.REACT_APP_BUILD_COMMIT,
        branch: process.env.REACT_APP_BUILD_BRANCH,
        environment: process.env.REACT_APP_ENVIRONMENT,
        buildTime: process.env.REACT_APP_BUILD_TIMESTAMP,
      });
    }
  }, []);

  return (
    <>
    <NetworkStatusBanner />
    <ConfirmDialogProvider>
    <KeycloakProvider key={retryKey}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="App">
            <Toaster
              position="top-right"
              containerStyle={{
                // Below the navbar (h-14 = 56px + margin), and below the pinned sync card
                // when one is on screen — SyncProgressCardSlot sets the variable.
                top: 'var(--panther-toast-top, 70px)',
                zIndex: 99, // Below dropdowns (z-100+) but above content
              }}
              toastOptions={{
                // Chrome, width and text metrics now live in ToastCard (below), which every
                // toast renders in — these only cover the defaults react-hot-toast applies
                // before our render prop sees the toast.
                duration: 4000,
              }}
            >
              {(t) => {
                // Custom toasts (e.g. the sync card) render their own complete UI.
                // Bypass the card so they aren't double-wrapped.
                if (t.type === 'custom') {
                  const msg = t.message;
                  return (typeof msg === 'function' ? msg(t) : msg) as React.ReactElement;
                }
                // Everything else — every plain toast.success/error/loading in the app —
                // gets the same card. Callers that only wrote a sentence get a title from
                // the toast's type; callers with something better to say use utils/notify.
                const message = typeof t.message === 'function' ? t.message(t) : t.message;
                const isText = typeof message === 'string' || typeof message === 'number';
                return (
                  <ToastCard
                    tone={toastToneFor(t.type)}
                    isDark={!lightThemeActive}
                    visible={t.visible}
                    onDismiss={() => hotToast.dismiss(t.id)}
                    {...(isText
                      ? { title: toastTitleFor(t.type), body: message }
                      : { children: message })}
                  />
                );
              }}
            </Toaster>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                {/* PUBLIC ROUTES - No authentication required */}
                <Route path="/landingpage" element={<LandingPage />} />
                {/* Per-customer onboarding guide, e.g. /landingpage/cond-aroeiras.
                    Public: KeycloakContext skips auth for anything under /landingpage. */}
                <Route path="/landingpage/:slug" element={<CustomerLandingPage />} />
                <Route path="/instalar-android" element={<AndroidInstall />} />
                <Route path="/privacidade" element={<PrivacyPolicy />} />
                <Route path="/politica-csae" element={<PoliticaCSAE />} />
                <Route path="/solicitar-exclusao" element={<SolicitarExclusao />} />
                <Route path="/suporte" element={<Suporte />} />
                <Route path="/pre-register" element={
                  <OperationalSettingsProvider>
                    <PublicPreRegistration />
                  </OperationalSettingsProvider>
                } />
                <Route path="/invite/:token" element={<InviteRegistration />} />
                <Route path="/r/:token" element={<ResidentSelfRegistration />} />
                <Route path="/convidados/:token" element={<PublicGuestList />} />
                <Route path="/kiosk/:customerId" element={<KioskPage />} />

                {/* AUTHENTICATED ROUTES - Wrapped in AuthGuard */}
                <Route path="/*" element={
                  <AuthGuard>
                    <AuthProvider>
                      <AccessDeniedProvider>
                        <ConnectionStatusProvider>
                          <SystemHealthProvider>
                            <CriticalErrorProvider>
                              <CustomerProvider>
                                <CustomerFilterProvider>
                                  <NotificationPreferencesProvider>
                                    <OperationalSettingsProvider>
                                      <NotificationProvider>
                                        <PersonDetailProvider>
                                          <SyncProgressProvider>
                                          <DesktopAccessGate>
                                          <PersonDetailModal />
                                          <SyncProgressModal />
                                          <SyncProgressCardSlot />
                                          <DeviceRegistrationEffect />
                                          <MenuEventListeners />
                                          <UpdateStatusListener />
                                          <ErrorOverlay />
                                          <Routes>
                                            <Route path="/" element={<Layout />}>
                      <Route index element={<Navigate to="/dashboard" replace />} />

                      {/* Dashboard - Role-based */}
                      <Route path="dashboard" element={<DashboardRouter />} />

                      {/* System Reports - Admin only */}
                      <Route path="system/reports" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <GlobalReports />
                        </RoleBasedRoute>
                      } />

                      {/* Access Control - All roles */}
                      <Route path="people" element={<People />} />
                      <Route path="people/:personId" element={<PersonProfile />} />
                      <Route path="facial" element={<Facial />} />
                      <Route path="access-logs" element={<AccessLogs />} />
                      <Route path="access-logs/*" element={<AccessLogs />} />
                      <Route path="access-control" element={<AccessControl />} />
                      <Route path="eventos-gerais" element={<EventosGerais />} />

                      {/* Zones & Devices - Admin/Manager only */}
                      <Route path="zones-devices" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <ZonesDevices />
                        </RoleBasedRoute>
                      } />

                      {/* Old Zones route - kept for backward compatibility */}
                      <Route path="zones" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <Zones />
                        </RoleBasedRoute>
                      } />

                      {/* Access Rules - Admin/Manager only */}
                      <Route path="access-rules" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <AccessRules />
                        </RoleBasedRoute>
                      } />

                      {/* Zone Groups - Admin/Manager only */}
                      <Route path="zone-groups" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <ZoneGroups />
                        </RoleBasedRoute>
                      } />

                      {/* Time Zones - Admin/Manager only */}
                      <Route path="time-zones" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <TimeZones />
                        </RoleBasedRoute>
                      } />

                      {/* Access Profiles - Admin/Manager only */}
                      <Route path="access-profiles" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <AccessProfiles />
                        </RoleBasedRoute>
                      } />

                      {/* Registration Cameras - Admin/Manager only */}
                      <Route path="registration-cameras" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <RegistrationCameras />
                        </RoleBasedRoute>
                      } />

                      {/* Customers - Admin only */}
                      <Route path="customers/stats" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <CustomerStats />
                        </RoleBasedRoute>
                      } />
                      <Route path="customers" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <Customers />
                        </RoleBasedRoute>
                      } />
                      <Route path="customers/*" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <Customers />
                        </RoleBasedRoute>
                      } />
                      <Route path="condos" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <Condos />
                        </RoleBasedRoute>
                      } />

                      {/* Structures - Admin/Manager + Administrador */}
                      <Route path="blocks" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <Blocks />
                        </RoleBasedRoute>
                      } />
                      <Route path="units" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <Units />
                        </RoleBasedRoute>
                      } />
                      <Route path="common-areas" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <CommonAreas />
                        </RoleBasedRoute>
                      } />
                      <Route path="common-area-reservations" element={<CommonAreaReservations />} />
                      <Route path="obras" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <Obras />
                        </RoleBasedRoute>
                      } />
                      <Route path="keys" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER, RoleCategory.STAFF]}>
                          <Keys lightTheme={true} />
                        </RoleBasedRoute>
                      } />
                      <Route path="pets" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER, RoleCategory.STAFF]}>
                          <Pets />
                        </RoleBasedRoute>
                      } />
                      <Route path="tags" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER, RoleCategory.STAFF]}>
                          <Tags />
                        </RoleBasedRoute>
                      } />
                      <Route path="normas-internas" element={<NormasInternas />} />
                      <Route path="ocorrencias" element={<Ocorrencias />} />
                      <Route path="livro" element={<Livro />} />
                      <Route path="achados-perdidos" element={<AchadosPerdidos />} />
                      <Route path="mural-anuncios" element={<MuralAnuncios />} />
                      <Route path="reconciliation" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <Reconciliation />
                        </RoleBasedRoute>
                      } />
                      <Route path="person-devices" element={<PersonDevices />} />

                      {/* Empresa Management - Admin/Manager + Administrador */}
                      <Route path="departments" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <Departments />
                        </RoleBasedRoute>
                      } />
                      <Route path="departments/:departmentId" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <DepartmentDetail />
                        </RoleBasedRoute>
                      } />
                      <Route path="employees" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <Employees />
                        </RoleBasedRoute>
                      } />
                      <Route path="suppliers" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <Suppliers />
                        </RoleBasedRoute>
                      } />
                      <Route path="vehicles" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <Vehicles />
                        </RoleBasedRoute>
                      } />

                      {/* People Management */}
                      <Route path="residents" element={<Residents />} />
                      <Route path="condo-employees" element={<CondoEmployees />} />
                      <Route path="visitors" element={<Visitors />} />
                      {/* Every clearance in the condomínio — the Cadastro counterpart of the
                          Operational View's Liberados tab. */}
                      <Route path="liberados" element={<Liberados />} />
                      {/* The app's request queue — credentials and identity photos — outside the
                          Operational View, where the sidebar app can reach it. */}
                      <Route path="solicitacoes" element={<Solicitacoes />} />
                      {/* Service Providers (Prestadores de Serviço) */}
                      <Route path="service-providers" element={<ServiceProviders />} />
                      <Route path="prestadores" element={<ServiceProviders />} />
                      <Route path="staff" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <Staff />
                        </RoleBasedRoute>
                      } />

                      {/* Administration - Admin only */}
                      <Route path="roles" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <Roles />
                        </RoleBasedRoute>
                      } />
                      <Route path="user-administration" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <UserAdministration />
                        </RoleBasedRoute>
                      } />
                      <Route path="admin/device-migration" element={
                        <RoleBasedRoute requiredRoles={['super_admin', 'condo_super_admin']}>
                          <AdminDeviceMigration />
                        </RoleBasedRoute>
                      } />
                      <Route path="admin/unauthorized-persons" element={
                        <RoleBasedRoute requiredRoles={['super_admin', 'admin']}>
                          <UnauthorizedPersons />
                        </RoleBasedRoute>
                      } />
                      <Route path="users" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <UnifiedUserAdmin />
                        </RoleBasedRoute>
                      } />
                      {/* The page gates itself to one person (see appVersionPolicy.ts); the
                          ADMIN category here is the outer guard, not the real one. */}
                      <Route path="app-versions" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <AppVersionPolicies />
                        </RoleBasedRoute>
                      } />
                      <Route path="users/:userId/profile" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <UserProfile />
                        </RoleBasedRoute>
                      } />
                      <Route path="users/*" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <UnifiedUserAdmin />
                        </RoleBasedRoute>
                      } />

                      {/* Customer Administration - Per-customer user view (Admin/Manager + Administrador) */}
                      <Route path="customer-admin" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador']}>
                          <CustomerAdministration />
                        </RoleBasedRoute>
                      } />

                      {/* Customer Settings - Admin only */}
                      <Route path="customer-settings" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <CustomerSettings />
                        </RoleBasedRoute>
                      } />

                      {/* Connected Devices - Admin only */}
                      <Route path="connected-devices" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <ConnectedDevices />
                        </RoleBasedRoute>
                      } />

                      {/* Device Audit - Admin/Manager only */}
                      <Route path="device-audit" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <DeviceAuditLogs />
                        </RoleBasedRoute>
                      } />

                      {/* Role Audit - Admin/Super Admin only */}
                      <Route path="role-audit" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <RoleAuditPage />
                        </RoleBasedRoute>
                      } />

                      {/* Integrations - Admin only */}
                      <Route path="integrations" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <Integrations />
                        </RoleBasedRoute>
                      } />

                      {/* Downloads & Release Notes - Admin (all) + Síndico (latest only) */}
                      <Route path="downloads" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <Downloads />
                        </RoleBasedRoute>
                      } />

                      {/* Kiosk Management - Síndico and above */}
                      <Route path="kiosk-management" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <KioskManagement />
                        </RoleBasedRoute>
                      } />

                      {/* QR Code Management - Admin/Manager only */}
                      <Route path="qr-codes" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <CustomerQRCodeManagement />
                        </RoleBasedRoute>
                      } />

                      {/* Import Data - Admin/Manager (Síndico) */}
                      <Route path="import-data" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <ImportData />
                        </RoleBasedRoute>
                      } />

                      {/* Import Data - File Wizard */}
                      <Route path="import-data/file" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <ImportDataFileWizard />
                        </RoleBasedRoute>
                      } />

                      {/* Import Data - Device Wizard */}
                      <Route path="import-data/device" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <ImportDataDeviceWizard />
                        </RoleBasedRoute>
                      } />

                      {/* Export Data - Admin/Manager (Síndico) */}
                      <Route path="export-data" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <ExportData />
                        </RoleBasedRoute>
                      } />

                      {/* Reports & Analytics - Admin/Manager/Staff (porteiro when enabled) */}
                      <Route path="reports" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER, RoleCategory.STAFF]}>
                          <Dashboard />
                        </RoleBasedRoute>
                      } />
                      <Route path="analytics" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]}>
                          <Dashboard />
                        </RoleBasedRoute>
                      } />
                      <Route path="audit" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <AuditLogs />
                        </RoleBasedRoute>
                      } />
                      <Route path="audit/*" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN]}>
                          <AuditLogs />
                        </RoleBasedRoute>
                      } />
                      {/* Encomendas - Package Management */}
                      <Route path="encomendas" element={
                        <RoleBasedRoute allowedCategories={[RoleCategory.ADMIN, RoleCategory.MANAGER]} requiredRoles={['administrador', 'sindico', 'porteiro', 'supervisor']}>
                          <Encomendas />
                        </RoleBasedRoute>
                      } />

                      {/* Photo Quality Audit - Electron only, nav item handles visibility */}
                      <Route path="photo-quality" element={<PhotoQualityAuditPage />} />

                      {/* Profile & Settings - All roles */}
                      <Route path="profile/user/:userId" element={<UserProfile />} />
                      <Route path="profile/user" element={<UserProfile />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="profile/*" element={<Profile />} />
                      <Route path="settings" element={<Profile />} />
                      <Route path="settings/*" element={<Profile />} />
                      <Route path="notifications" element={<NotificationCenter />} />
                      <Route path="notification-settings" element={<NotificationSettings />} />

                      {/* Support - All roles */}
                      <Route path="support" element={<Support />} />
                      <Route path="support/*" element={<Support />} />
                      <Route path="help/*" element={<Support />} />

                      {/* Avisos e Atualizações - All roles */}
                      <Route path="avisos-atualizacoes" element={<AvisosAtualizacoes />} />

                      {/* Fallback */}
                      <Route path="*" element={<DashboardRouter />} />
                    </Route>
                  </Routes>
                                          </DesktopAccessGate>
                          </SyncProgressProvider>
                        </PersonDetailProvider>
                      </NotificationProvider>
                    </OperationalSettingsProvider>
                  </NotificationPreferencesProvider>
                </CustomerFilterProvider>
              </CustomerProvider>
            </CriticalErrorProvider>
          </SystemHealthProvider>
        </ConnectionStatusProvider>
      </AccessDeniedProvider>
    </AuthProvider>
  </AuthGuard>
                } />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </QueryClientProvider>
    </KeycloakProvider>
    </ConfirmDialogProvider>
    </>
  );
}

function AppWithBoundary() {
  return (
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
}

export default AppWithBoundary;
